import React, { createContext, FC, PropsWithChildren, ReactNode, useContext, useState } from "react";
import { useStoreSelector } from "../../hooks/StoreHooks";
import { NullableString } from "../core/common_types";
import rals from "../assets/rals.json";
import finishes from "../assets/finishes.json";
import * as THREE from 'three';

type FinishContextData = {
    getImgPreview: (code: NullableString) => Promise<HTMLImageElement | undefined>
    getCodeName: (code: NullableString) => string
    getImgPreviewFromBase64: (base64: NullableString, width: number, height: number, contain: boolean) => Promise<HTMLImageElement | undefined>
    getRalPreview: (ral: NullableString) => ReactNode
    getRalName: (ral: NullableString) => string
    createImgPreviewFromFile: (file: File | undefined) => Promise<string | null>
    createTexture: (code: NullableString) => Promise<THREE.Texture | undefined>
}

const FinishContext = createContext<FinishContextData | null>(null);

const FinishProvider: FC<{ children?: ReactNode }> = ({ children }) => {
    // Sorgente immagine di un codice: le tinte RAL diventano un quadrato pieno generato
    // al volo, le finiture (woodstyle, HPL, pietravera...) puntano al campione in /img.
    const getImgSource = (code: NullableString): NullableString => {
        if (!code) return null;

        let ral = rals.find(r => r.RAL == code);
        if (ral) {
            const canvas = document.createElement('canvas');
            canvas.width = 800;
            canvas.height = 800;

            const ctx = canvas.getContext('2d');
            if (ctx) {
                ctx.fillStyle = ral.HEX;
                ctx.fillRect(0, 0, canvas.width, canvas.height);

                return canvas.toDataURL('image/jpeg');
            }
            return null;
        }

        return finishes.find(f => f.code == code)?.img ?? null;
    }

    /**
     * Carica l'immagine di una finitura.
     * @param size se valorizzato forza gli attributi width/height dell'elemento (serve al
     *             disegno 2D su canvas); va lasciato indefinito per le texture 3D, dove
     *             three.js usa img.width/img.height per allocare la texture sulla GPU:
     *             con una misura diversa da quella reale (es. i campioni T01/T02 sono
     *             333x333, non 800x800 come dice il nome del file) i pixel veri finiscono
     *             in un angolo e il resto della texture resta nero.
     */
    const loadImg = (imgSource: NullableString, size?: number) => {
        return new Promise<HTMLImageElement | undefined>(resolve => {
            if (!imgSource) {
                resolve(undefined);
                return;
            }

            const img = size ? new Image(size, size) : new Image();
            // crossOrigin e referrerPolicy vanno impostati PRIMA di src: cambiarli dopo
            // fa ripartire il caricamento dell'immagine, cosi' onload risolve un elemento
            // che il browser sta ancora ri-decodificando.
            img.crossOrigin = "anonymous";
            img.referrerPolicy = "origin";
            img.onload = async () => {
                // decode() garantisce che i pixel siano pronti: la texture 3D finisce sulla
                // GPU una volta sola, se l'immagine non e' completa resta nera per sempre.
                try { await img.decode(); } catch (e) { /* gia' decodificata o decode non supportato */ }
                resolve(img);
            };
            img.onerror = () => resolve(undefined);
            img.src = imgSource;
        });
    }

    const getImgPreview = (code: NullableString) => {
        return loadImg(getImgSource(code), 800);
    }
    const createTexture = async (code: NullableString) => {
        const img = await loadImg(getImgSource(code));
        if (!img) return undefined;
        const txt = new THREE.Texture(img);
        // Ripetizione a specchio: i campioni di finitura non sono seamless, affiancandoli
        // tali e quali si vedrebbe il bordo netto di ogni tile (effetto scacchiera).
        // Specchiati, i lati adiacenti combaciano e la superficie risulta continua.
        txt.wrapS = THREE.MirroredRepeatWrapping;
        txt.wrapT = THREE.MirroredRepeatWrapping;
        txt.colorSpace = THREE.SRGBColorSpace;
        txt.minFilter = THREE.LinearMipmapLinearFilter;
        txt.magFilter = THREE.LinearFilter;
        txt.generateMipmaps = true;
        txt.anisotropy = 8;
        txt.needsUpdate = true;
        return txt;
    }
    const getImgPreviewFromBase64 = (base64: NullableString, width: number, height: number, contain: boolean) => {
        return new Promise<HTMLImageElement | undefined>(resolve => {
            if (!base64) {
                resolve(undefined);
                return;
            }

            var img = new Image();
            img.onload = () => {
                const canvas = document.createElement('canvas');

                const ctx = canvas.getContext('2d');
                if (!ctx) return;

                // Impostiamo la dimensione del canvas
                canvas.width = width;
                canvas.height = height;

                // Calcoliamo il rapporto di aspetto originale
                const aspectRatio = img.width / img.height;

                if (contain) {
                    // Calcoliamo le nuove dimensioni mantenendo il rapporto di aspetto
                    let newWidth, newHeight;

                    if (img.width > img.height) {
                        newWidth = width;
                        newHeight = Math.round(width / aspectRatio);
                    } else {
                        newHeight = height;
                        newWidth = Math.round(height * aspectRatio);
                    }

                    // Se l'immagine è più alta di larga, la facciamo adattare all'altezza
                    if (newHeight > height) {
                        newHeight = height;
                        newWidth = Math.round(height * aspectRatio);
                    }

                    // Se l'immagine è più larga di alta, la facciamo adattare alla larghezza
                    if (newWidth > width) {
                        newWidth = width;
                        newHeight = Math.round(width / aspectRatio);
                    }

                    // Centriamo l'immagine nel canvas
                    const xOffset = (width - newWidth) / 2;
                    const yOffset = (height - newHeight) / 2;

                    // Disegniamo l'immagine ridimensionata nel canvas
                    ctx.fillStyle = "#ffffff";
                    ctx.fillRect(0, 0, canvas.width, canvas.height);
                    ctx.drawImage(img, xOffset, yOffset, newWidth, newHeight);

                    const imgResized = new Image();
                    imgResized.onload = () => {
                        resolve(imgResized);
                    };
                    imgResized.src = canvas.toDataURL("image/png");
                } else {
                    // Calcoliamo le dimensioni del canvas mantenendo il rapporto di aspetto
                    let canvasWidth, canvasHeight;

                    // Se il rapporto di aspetto dell'immagine è maggiore (più larga che alta)
                    if (aspectRatio > 1) {
                        // Se l'immagine è più alta che larga
                        canvasHeight = height;
                        canvasWidth = Math.round(height * aspectRatio);                        
                    } else {
                        canvasWidth = width;
                        canvasHeight = Math.round(width / aspectRatio);                        
                    }

                    // Calcoliamo la posizione per centrare l'immagine
                    const xOffset = (width - canvasWidth) / 2;
                    const yOffset = (height - canvasHeight) / 2;

                    // Disegniamo l'immagine nel canvas con il taglio appropriato
                    ctx.fillStyle = "#ffffff";
                    ctx.fillRect(0, 0, canvas.width, canvas.height);
                    ctx.drawImage(img, 0, 0, img.width, img.height, xOffset, yOffset, canvasWidth, canvasHeight);

                    const imgResized = new Image();
                    imgResized.onload = () => {
                        resolve(imgResized);
                    };
                    imgResized.src = canvas.toDataURL("image/png");
                }

                
            };
            img.src = base64;
        });
    }

    const createImgPreviewFromFile = (file: File | undefined) => {
        return new Promise<string | null>(resolve => {
            if (!file) {
                resolve(null);
                return;
            }

            var img = new Image();
            img.onload = () => {
                const canvas = document.createElement('canvas');
                canvas.width = img.naturalWidth;
                canvas.height = img.naturalHeight;

                const ctx = canvas.getContext('2d');
                if (!ctx) return;

                ctx.fillStyle = "#ffffff";
                ctx.fillRect(0, 0, canvas.width, canvas.height);
                ctx.drawImage(img, 0, 0);

                const dataURL = canvas.toDataURL("image/jpeg");
                resolve(dataURL);
            };
            img.crossOrigin = "Anonymous";
            img.referrerPolicy = "origin";
            img.src = URL.createObjectURL(file);            
        });
    }
    const getRalPreview = (ral: NullableString): ReactNode => {
        if (!ral) return null;

        let preview = rals.find(f => f.RAL == ral)?.HEX;
        if (preview) return <div style={{ width: "1.75rem", height: "1.75rem", backgroundColor: preview }}></div>;

        return null;
    }

    const getRalName = (ral: NullableString): string => {
        if (!ral) return "";

        let resRal = rals.find(f => f.RAL == ral);
        if (resRal) return resRal.Italian + ` (${ral})`;

        return ral;
    }
    const getCodeName = (code: NullableString): string => {
        if (!code) return "";

        let resRal = finishes.find(f => f.code == code);
        if (resRal) return resRal.Italian + ` (${code})`;

        return code;
    }
    return (
        <FinishContext.Provider value={{ getImgPreview,getCodeName, getImgPreviewFromBase64, getRalPreview, getRalName, createImgPreviewFromFile,createTexture }}>
            {children}
        </FinishContext.Provider>
    );
}

export default FinishProvider;

export const useFinishes = () => {
    return useContext(FinishContext);
}